import React, { useState, useRef } from 'react';
import '../css/Efficiency.css';
import DatePicker from 'react-datepicker'; // If using datepicker
import 'react-datepicker/dist/react-datepicker.css';

// Toast imports
import { toast } from 'react-toastify'; // Import ToastContainer
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { VscFileSubmodule } from 'react-icons/vsc';
import { RxLightningBolt } from 'react-icons/rx';
import { IoMdGlobe } from 'react-icons/io';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { IoMdDownload } from 'react-icons/io';
import { GiBrazilFlag } from 'react-icons/gi';
import { FaGithub } from 'react-icons/fa6';
import { GiUsaFlag } from 'react-icons/gi';
import { LuRefreshCw } from 'react-icons/lu';

export interface EFFFile {
  name: string;
  url: string;
}

export type AllServerEFFFilesObject = {
  [siteName: string]: EFFFile[];
};

export interface EFFList {
  data: AllServerEFFFilesObject;
}

const EffView: React.FC = () => {
  const [loadingStatesEFF, setLoadingStatesEFF] = useState<{
    usa: boolean;
    br: boolean;
  }>({
    usa: false,
    br: false,
  });

  const setLoadingUSA = (value: boolean) => {
    setLoadingStatesEFF((prev) => ({ ...prev, usa: value }));
  };
  const setLoadingBR = (value: boolean) => {
    setLoadingStatesEFF((prev) => ({ ...prev, br: value }));
  };

  const [effListUSA, setEffListUSA] = useState<AllServerEFFFilesObject | null>(
    null
  );
  const [effListBR, setEffListBR] = useState<AllServerEFFFilesObject | null>(
    null
  );

  const fetchEFFList = async (source: string) => {
    try {
      // Fetch data from Django API
      const url = new URL(
        '/apidet/detector/efficiency',
        window.location.origin
      );

      url.pathname += `/${source}`; // Append the source to the URL path

      // Fetch data from Django API
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', // Or 'include' based on your need
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for errors in the response
      if (data.error) {
        displayNotification(false, 'Error from ' + source + ': ' + data.error);
        console.error('Error from server:', data.error);
        // You can handle the error accordingly, e.g., show a notification
        setLoadingUSA(false);
        setLoadingBR(false);
        return; // Exit the function if there's an error
      }

      // Check if dataUSA exists and set it
      // Set the state based on the source
      if (source === 'dataUSA') {
        setEffListUSA(data); // Assuming data is the correct structure
        setLoadingUSA(false);
      } else if (source === 'dataBR') {
        setEffListBR(data); // Assuming data is the correct structure
        setLoadingBR(false);
      } else {
        console.error('Error: Invalid source provided:', source);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingUSA(false);
      setLoadingBR(false);
    }
  };

  const fetchREFList = async (source: string) => {
    try {
      // Fetch data from Django API
      const url = new URL(
        '/apidet/detector/referencedata',
        window.location.origin
      );

      url.pathname += `/${source}`; // Append the source to the URL path

      // Fetch data from Django API
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', // Or 'include' based on your need
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check for errors in the response
      if (data.error) {
        displayNotification(false, 'Error from ' + source + ': ' + data.error);
        console.error('Error from server:', data.error);
        // You can handle the error accordingly, e.g., show a notification
        setLoadingUSARef(false);
        setLoadingBRRef(false);
        return; // Exit the function if there's an error
      }

      // Check if dataUSA exists and set it
      // Set the state based on the source
      if (source === 'dataUSA') {
        setRefListUSA(data); // Assuming data is the correct structure
        setLoadingUSARef(false);
      } else if (source === 'dataBR') {
        setRefListBR(data); // Assuming data is the correct structure
        setLoadingBRRef(false);
      } else {
        console.error('Error: Invalid source provided:', source);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingUSARef(false);
      setLoadingBRRef(false);
    }
  };

  const downloadData = async (
    source: string,
    filename: string,
    filepath: string,
    type: 'efficiency' | 'referencedata'
  ) => {
    try {
      // Fetch data from Django API
      const url = new URL(
        `/apidet/detector/${type}/download`,
        window.location.origin
      );

      url.pathname += `/${source}`; // Append the source to the URL path
      url.pathname += `/${filename}`; // Append the source to the URL path
      // Fetch data from Django API
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json',
          'X-Filepath': filepath,  // Send the filepath in the headers
         },
        credentials: 'same-origin', // Or 'include' based on your need
      });

      if (!response.ok) {
        displayNotification(
          false,
          'Error from ' + source + ': ' + response.status
        );
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create a Blob from the response
      displayNotification(true, 'Downloading  ' + filename + ' from ' + source);
      const blob = await response.blob();
      const urlBlob = window.URL.createObjectURL(blob);

      // Create a link element to trigger the download
      const link = document.createElement('a');
      link.href = urlBlob;
      link.setAttribute('download', filename); // Set the filename for the download
      document.body.appendChild(link);
      link.click(); // Trigger the download
      // Clean up: remove the link from the DOM if it has a parent
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const [loadingStatesREF, setLoadingStatesREF] = useState<{
    usa: boolean;
    br: boolean;
  }>({
    usa: false,
    br: false,
  });

  const setLoadingUSARef = (value: boolean) => {
    setLoadingStatesREF((prev) => ({ ...prev, usa: value }));
  };
  const setLoadingBRRef = (value: boolean) => {
    setLoadingStatesREF((prev) => ({ ...prev, br: value }));
  };

  const [refListUSA, setRefListUSA] = useState<AllServerEFFFilesObject | null>(
    null
  );
  const [refListBR, setRefListBR] = useState<AllServerEFFFilesObject | null>(
    null
  );

  function displayNotification(successOrFail: boolean, message: string) {
    // Show error toast
    if (successOrFail) {
      toast.success(message, {
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#2d2f31',
          color: 'white',
          fontSize: '16px',
        },
      });
    } else {
      toast.error(message, {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
          backgroundColor: '#2d2f31',
          color: 'white',
          fontSize: '16px',
        },
      });
    }
  }

  // USA Filters
  const [filterTypeEFFUSA, setFilterTypeEFFUSA] = useState<
    'all' | 'lightning' | 'global' | 'date'
  >('all');
  const [selectedDateEFFUSA, setSelectedDateEFFUSA] = useState<Date | null>(
    null
  );
  const datePickerRefEFFUSA = useRef<DatePicker | null>(null);

  const [filterTypeREFUSA, setFilterTypeREFUSA] = useState<'all' | 'date'>(
    'all'
  );
  const [selectedDateREFUSA, setSelectedDateREFUSA] = useState<Date | null>(
    null
  );
  const datePickerRefREFUSA = useRef<DatePicker | null>(null);

  // BR Filters
  const [filterTypeEFFBR, setFilterTypeEFFBR] = useState<
    'all' | 'lightning' | 'global' | 'date'
  >('all');
  const [selectedDateEFFBR, setSelectedDateEFFBR] = useState<Date | null>(null);
  const datePickerRefEFFBR = useRef<DatePicker | null>(null);

  const [filterTypeREFBR, setFilterTypeREFBR] = useState<'all' | 'date'>('all');
  const [selectedDateREFBR, setSelectedDateREFBR] = useState<Date | null>(null);
  const datePickerRefREFBR = useRef<DatePicker | null>(null);

  const [showEFFUSA, setShowEFFUSA] = useState(false);
  const [showREFUSA, setShowREFUSA] = useState(false);
  const [showEFFBR, setShowEFFBR] = useState(false);
  const [showREFBR, setShowREFBR] = useState(false);

  function parseEFFFile(fileName: string) {
    const format = fileName.split('.').pop() || '';
    let type = 'Unknown';
    let date = '';
    let icon = null;

    if (fileName.includes('lightning')) {
      type = 'lightning';
      icon = <RxLightningBolt className="eff-icon" />;
      const match = fileName.match(/_(\d{4}-\d{2}-\d{2})/); // after first underscore
      if (match) date = match[1];
    } else if (fileName.includes('Global')) {
      type = 'global';
      icon = <IoMdGlobe className="eff-icon" />;
      const match = fileName.match(/_(\d{4}-\d{2}-\d{2})/); // match first date in Global filename
      if (match) date = match[1];
    }

    return { format, type, date, icon };
  }

  function parseRefFile(fileName: string) {
    const format = fileName.split('.').pop() || '';
    let date = '';

    const match = fileName.match(/(\d{4}-\d{2}-\d{2})/); // gets the first date
    if (match) date = match[1];

    return { format, date };
  }

  const handleFetch = (
    open: boolean,
    setShow: React.Dispatch<React.SetStateAction<boolean>>,
    setLoading: (value: boolean) => void,
    dataServer: string,
    fetch: (dataServer: string) => void,
    list: AllServerEFFFilesObject | null
  ) => {
    if (open) {
      setShow(true);
      setLoading(true);
      fetch(dataServer);
    } else {
      setShow((prev) => !prev);
      if (!list) {
        setLoading(true);
        fetch(dataServer);
      }
    }
  };

  type FilterTypeEFF = 'all' | 'lightning' | 'global' | 'date';
  interface EffButtonDashProps {
    fetchEFF: () => void;
    effList: AllServerEFFFilesObject | null;
    showEFF: boolean;
    setFilterTypeEFF: (value: FilterTypeEFF) => void;
    setSelectedDateEFF: (date: Date | null) => void;
    datePickerRefEFF: React.RefObject<DatePicker>;
    selectedDateEFF: Date | null;
    filterTypeEFF: string;
    dataServer: string;
    loadingState: boolean;
  }

  const EffButtonDash: React.FC<EffButtonDashProps> = ({
    fetchEFF,
    effList,
    showEFF,
    setFilterTypeEFF,
    setSelectedDateEFF,
    datePickerRefEFF,
    selectedDateEFF,
    filterTypeEFF,
    dataServer,
    loadingState,
  }) => {
    return (
      <>
        {effList && showEFF && (
          <>
            {/* Calculate dynamic stats */}
            {(() => {
              const allFiles = Object.values(effList).flat();
              const totalFiles = allFiles.length;
              const lightningFiles = allFiles.filter((file) =>
                parseEFFFile(file.name).type.toLowerCase().includes('lightning')
              ).length;
              const globalFiles = allFiles.filter((file) =>
                parseEFFFile(file.name).type.toLowerCase().includes('global')
              ).length;
              const uniqueMonths = new Set(
                allFiles.map((file) => parseEFFFile(file.name).date.slice(0, 7)) // YYYY-MM
              ).size;

              return (
                <div className="efficiency-card-stack">
                  <div
                    className="efficiency-card"
                    onClick={() => {
                      setFilterTypeEFF('all');
                      setSelectedDateEFF(null);
                    }}
                  >
                    <h1>
                      <VscFileSubmodule /> All Files
                    </h1>
                    <h1>{totalFiles}</h1>
                  </div>

                  <div
                    className="efficiency-card"
                    onClick={() => setFilterTypeEFF('lightning')}
                  >
                    <h1>
                      <RxLightningBolt /> Lightning Files
                    </h1>
                    <h1>{lightningFiles}</h1>
                  </div>

                  <div
                    className="efficiency-card"
                    onClick={() => setFilterTypeEFF('global')}
                  >
                    <h1>
                      <IoMdGlobe /> Global Files
                    </h1>
                    <h1>{globalFiles}</h1>
                  </div>

                  <div className="date-card-and-picker">
                    <div
                      className="efficiency-card"
                      onClick={() => {
                        setFilterTypeEFF('date');
                        setSelectedDateEFF(null);
                        setTimeout(() => {
                          if (datePickerRefEFF.current) {
                            datePickerRefEFF.current.setOpen(true);
                          }
                        }, 0);
                      }}
                    >
                      <h1>
                        <FaRegCalendarAlt /> Months
                      </h1>
                      <h1>{uniqueMonths}</h1>
                    </div>

                    {filterTypeEFF === 'date' && (
                      <div className="testing-something">
                        <DatePicker
                          ref={datePickerRefEFF}
                          selected={selectedDateEFF}
                          onChange={(date) => setSelectedDateEFF(date)}
                          dateFormat="yyyy-MM-dd"
                          placeholderText=""
                          isClearable
                          showPopperArrow={false}
                        />
                      </div>
                    )}
                  </div>

                  <div className="efficiency-card" onClick={fetchEFF}>
                    <LuRefreshCw
                      className={`refresh-button ${
                        loadingState ? 'rotating' : ''
                      }`}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Table */}
            <div className="eff-table-wrapper">
              <div className="efficiency-table">
                <div className="efficiency-header">
                  <p>Filename</p>
                  <p>Region</p>
                  <p>Type</p>
                  <p>Format</p>
                  <p>Date</p>
                  <p>Download</p>
                </div>

                {Object.entries(effList).flatMap(([siteName, files]) => {
                  return files
                    .map((file) => ({
                      ...file,
                      parsed: parseEFFFile(file.name),
                    }))
                    .filter(({ parsed }) => {
                      const type = parsed.type.toLowerCase();
                      const fileDate = parsed.date;

                      if (filterTypeEFF === 'lightning')
                        return type.includes('lightning');
                      if (filterTypeEFF === 'global')
                        return type.includes('global');
                      if (filterTypeEFF === 'date' && selectedDateEFF) {
                        const selectedDateStr = selectedDateEFF
                          .toISOString()
                          .split('T')[0];
                        return fileDate === selectedDateStr;
                      }

                      return true; // 'all'
                    })

                    .sort((a, b) => {
                      const dateA = new Date(a.parsed.date);
                      const dateB = new Date(b.parsed.date);
                      return dateB.getTime() - dateA.getTime(); // Newest first
                    })
                    .map(({ name, url, parsed }) => {
                      const { format, type, date, icon } = parsed;
                      return (
                        <div key={name} className="efficiency-item">
                          <p>{name}</p>
                          <p>{url.replace("/api/efficiency/download/", "").replace(name, "")}</p>
                          <div className="efficiency-icon">{icon}</div>
                          <p>{format}</p>
                          <p>{date}</p>
                          <p className="efficiency-icon">
                            <IoMdDownload
                              className="eff-download-icon"
                              onClick={() =>
                                downloadData(dataServer, name, url, 'efficiency')
                              }
                            />
                          </p>
                        </div>
                      );
                    });
                })}
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  type FilterTypeREF = 'all' | 'date';
  interface RefButtonDashProps {
    fetchREF: () => void;
    refList: AllServerEFFFilesObject | null;
    showREF: boolean;
    setFilterTypeREF: (value: FilterTypeREF) => void;
    setSelectedDateREF: (date: Date | null) => void;
    datePickerRefREF: React.RefObject<DatePicker>;
    selectedDateREF: Date | null;
    filterTypeREF: string;
    dataServer: string;
    loadingState: boolean;
  }

  const RefButtonDash: React.FC<RefButtonDashProps> = ({
    fetchREF,
    refList,
    showREF,
    setFilterTypeREF,
    setSelectedDateREF,
    datePickerRefREF,
    selectedDateREF,
    filterTypeREF,
    dataServer,
    loadingState,
  }) => {
    return (
      <>
        {refList && showREF && (
          <>
            {/* Calculate dynamic stats */}
            {(() => {
              const allFiles = Object.values(refList).flat();
              const totalFiles = allFiles.length;

              const uniqueMonths = new Set(
                allFiles.map((file) => parseRefFile(file.name).date.slice(0, 7)) // YYYY-MM
              ).size;

              return (
                <div className="efficiency-card-stack">
                  <div
                    className="efficiency-card"
                    onClick={() => {
                      setFilterTypeREF('all');
                      setSelectedDateREF(null);
                    }}
                  >
                    <h1>
                      <VscFileSubmodule /> All Files
                    </h1>
                    <h1>{totalFiles}</h1>
                  </div>

                  <div className="date-card-and-picker">
                    <div
                      className="efficiency-card"
                      onClick={() => {
                        setFilterTypeREF('date');
                        setSelectedDateREF(null);
                        setTimeout(() => {
                          if (datePickerRefREF.current) {
                            datePickerRefREF.current.setOpen(true);
                          }
                        }, 0);
                      }}
                    >
                      <h1>
                        <FaRegCalendarAlt /> Months
                      </h1>
                      <h1>{uniqueMonths}</h1>
                    </div>

                    {filterTypeREF === 'date' && (
                      <div className="testing-something">
                        <DatePicker
                          ref={datePickerRefREF}
                          selected={selectedDateREF}
                          onChange={(date) => setSelectedDateREF(date)}
                          dateFormat="yyyy-MM-dd"
                          placeholderText=""
                          isClearable
                          showPopperArrow={false}
                        />
                      </div>
                    )}
                  </div>

                  <div className="efficiency-card" onClick={fetchREF}>
                    <LuRefreshCw
                      className={`refresh-button ${
                        loadingState ? 'rotating' : ''
                      }`}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Table */}
            <div className="eff-table-wrapper">
              <div className="efficiency-table">
                <div className="efficiency-header">
                  <p>Filename</p>
                  <p>Region</p>
                  <p>Type</p>
                  <p>Format</p>
                  <p>Date</p>
                  <p>Download</p>
                </div>

                {Object.entries(refList).flatMap(([siteName, files]) => {
                  return files
                    .map((file) => ({
                      ...file,
                      parsed: parseRefFile(file.name),
                    }))
                    .filter(({ parsed }) => {
                      const fileDate = parsed.date;
                      if (filterTypeREF === 'date' && selectedDateREF) {
                        const selectedDateStr = selectedDateREF
                          .toISOString()
                          .split('T')[0];
                        return fileDate === selectedDateStr;
                      }
                      return true; // 'all'
                    })

                    .sort((a, b) => {
                      const dateA = new Date(a.parsed.date);
                      const dateB = new Date(b.parsed.date);
                      return dateB.getTime() - dateA.getTime(); // Newest first
                    })
                    .map(({ name, url, parsed }) => {
                      const { format, date } = parsed;
                      return (
                        <div key={name} className="efficiency-item">
                          <p>{name}</p>
                          <p>{url.replace("/api/referencedata/download/", "").replace(name, "")}</p>
                          <div className="efficiency-icon">
                            <VscFileSubmodule className="eff-icon" />
                          </div>
                          <p>{format}</p>
                          <p>{date}</p>
                          <p className="efficiency-icon">
                            <IoMdDownload
                              className="eff-download-icon"
                              onClick={() =>
                                downloadData(dataServer, name, url, 'referencedata')
                              }
                            />
                          </p>
                        </div>
                      );
                    });
                })}
              </div>
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="body">
      <div className="eff-button-and-dash">
        <a
          href="https://github.com/fire-neural-network/FNN_GLOBAL_EFF_Dashboard"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          <p className="link">
            {' '}
            <FaGithub className="eff-expand-icon-us" /> Streamlit Dashboard Code
          </p>
        </a>
      </div>

      <div className="eff-button-and-dash">
        <button
          onClick={() =>
            handleFetch(
              false,
              setShowEFFUSA,
              setLoadingUSA,
              'dataUSA',
              fetchEFFList,
              effListUSA
            )
          }
          className="eff-fetch-button"
        >
          <GiUsaFlag className="eff-expand-icon-us" /> Load Efficiency Data
        </button>

        <EffButtonDash
          fetchEFF={() =>
            handleFetch(
              true,
              setShowEFFUSA,
              setLoadingUSA,
              'dataUSA',
              fetchEFFList,
              effListUSA
            )
          }
          effList={effListUSA}
          showEFF={showEFFUSA}
          setFilterTypeEFF={setFilterTypeEFFUSA}
          setSelectedDateEFF={setSelectedDateEFFUSA}
          datePickerRefEFF={datePickerRefEFFUSA}
          selectedDateEFF={selectedDateEFFUSA}
          filterTypeEFF={filterTypeEFFUSA}
          dataServer={'dataUSA'}
          loadingState={loadingStatesEFF['usa']}
        />
      </div>

      <div className="eff-button-and-dash">
        <button
          onClick={() =>
            handleFetch(
              false,
              setShowREFUSA,
              setLoadingUSARef,
              'dataUSA',
              fetchREFList,
              refListUSA
            )
          }
          className="eff-fetch-button"
        >
          <GiUsaFlag className="eff-expand-icon-us" />
          Load Referece Data
        </button>

        <RefButtonDash
          fetchREF={() =>
            handleFetch(
              true,
              setShowREFUSA,
              setLoadingUSARef,
              'dataUSA',
              fetchREFList,
              refListUSA
            )
          }
          refList={refListUSA}
          showREF={showREFUSA}
          setFilterTypeREF={setFilterTypeREFUSA}
          setSelectedDateREF={setSelectedDateREFUSA}
          datePickerRefREF={datePickerRefREFUSA}
          selectedDateREF={selectedDateREFUSA}
          filterTypeREF={filterTypeREFUSA}
          dataServer={'dataUSA'}
          loadingState={loadingStatesREF['usa']}
        />
      </div>

      <div className="eff-button-and-dash">
        <button
          onClick={() =>
            handleFetch(
              false,
              setShowEFFBR,
              setLoadingBR,
              'dataBR',
              fetchEFFList,
              effListBR
            )
          }
          className="eff-fetch-button"
        >
          <GiBrazilFlag className="eff-expand-icon" />
          Load Efficiency Data
        </button>

        <EffButtonDash
          fetchEFF={() =>
            handleFetch(
              true,
              setShowEFFBR,
              setLoadingBR,
              'dataBR',
              fetchEFFList,
              effListBR
            )
          }
          effList={effListBR}
          showEFF={showEFFBR}
          setFilterTypeEFF={setFilterTypeEFFBR}
          setSelectedDateEFF={setSelectedDateEFFBR}
          datePickerRefEFF={datePickerRefEFFBR}
          selectedDateEFF={selectedDateEFFBR}
          filterTypeEFF={filterTypeEFFBR}
          dataServer={'dataBR'}
          loadingState={loadingStatesEFF['br']}
        />
      </div>

      <div className="eff-button-and-dash">
        <button
          onClick={() =>
            handleFetch(
              false,
              setShowREFBR,
              setLoadingBRRef,
              'dataBR',
              fetchREFList,
              refListBR
            )
          }
          className="eff-fetch-button"
        >
          <GiBrazilFlag className="eff-expand-icon" /> Load Referece Data
        </button>

        <RefButtonDash
          fetchREF={() =>
            handleFetch(
              true,
              setShowREFBR,
              setLoadingBRRef,
              'dataBR',
              fetchREFList,
              refListBR
            )
          }
          refList={refListBR}
          showREF={showREFBR}
          setFilterTypeREF={setFilterTypeREFBR}
          setSelectedDateREF={setSelectedDateREFBR}
          datePickerRefREF={datePickerRefREFBR}
          selectedDateREF={selectedDateREFBR}
          filterTypeREF={filterTypeREFBR}
          dataServer={'dataBR'}
          loadingState={loadingStatesREF['br']}
        />
      </div>
    </div>
  );
};

export default EffView;
